import React, { useState, useEffect } from 'react';
import { networks } from '../config/settings'; // Import the network configurations

const ConnectButton = ({ handleConnectionStatus }) => {
  const networkKeys = Object.keys(networks);
  const [account, setAccount] = useState(localStorage.getItem('connectedAccount') || '');
  const [currentNetwork, setCurrentNetwork] = useState(networkKeys[0]); // Default to the first network in the list
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  useEffect(() => {
    if (window.ethereum) {
      if (localStorage.getItem('walletDisconnected') !== 'true') {
        window.ethereum.request({ method: 'eth_accounts' }).then(handleAccountsChanged).catch(console.error);
        window.ethereum.request({ method: 'eth_chainId' }).then((chainId) => {
          updateNetworkState(chainId);
        });

        // Listen for account changes
        window.ethereum.on('accountsChanged', handleAccountsChanged);

        // Listen for network changes
        window.ethereum.on('chainChanged', (chainId) => {
          updateNetworkState(chainId);
          window.location.reload(); // Optional: Reload the page to ensure all data is fresh. Remove if not needed.
        });
      }
    }

    // Cleanup function to remove listeners when component unmounts
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', updateNetworkState);
      }
    };
  }, []);

  // Helper function to update network state based on chainId
  const updateNetworkState = (chainId) => {
    const matchingNetworkKey = networkKeys.find(key => networks[key].chainId === chainId);

    if (matchingNetworkKey) {
      setCurrentNetwork(matchingNetworkKey);
      setNetworkMismatch(false);
      setConnectionError('');
    } else {
      setNetworkMismatch(true);
      setConnectionError('Network mismatch. Please switch to a supported network.');
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      console.log('Please connect to your wallet.');
      setAccount('');
      localStorage.removeItem('connectedAccount');
      handleConnectionStatus(false);
    } else {
      setAccount(accounts[0]);
      localStorage.setItem('connectedAccount', accounts[0]);
      localStorage.removeItem('walletDisconnected'); // Remove the disconnect flag
      handleConnectionStatus(true);
    }
  };

  const handleNetworkChange = async (networkKey) => {
    const network = networks[networkKey];
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainId }],
      });
      setCurrentNetwork(networkKey);
      setNetworkMismatch(false);
      setConnectionError('');
    } catch (switchError) {
      if (switchError.code === 4001) { // User denied the switch request
        setConnectionError(`Network switch to ${network.chainName} denied.`);
      } else if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{ ...network }],
          });
          setCurrentNetwork(networkKey);
          setNetworkMismatch(false);
          setConnectionError('');
        } catch (addError) {
          setConnectionError(`Could not add ${network.chainName} network. Error: ${addError.message}`);
        }
      } else {
        setConnectionError(`Could not switch to ${network.chainName}. Error: ${switchError.message}`);
      }
    }
  };

  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        handleAccountsChanged(accounts);
      } catch (error) {
        if (error.code === 4001) {
          console.log('Please connect to MetaMask.');
        } else {
          console.error(error);
        }
      }
    } else {
      console.log('Please install MetaMask!');
    }
  };

  const disconnectWallet = () => {
    setAccount('');
    localStorage.removeItem('connectedAccount');
    localStorage.setItem('walletDisconnected', 'true');
    handleConnectionStatus(false);
    console.log('Wallet disconnected. Please also disconnect in MetaMask.');
  };

  const truncateAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  return (
    <div className="flex items-center space-x-4">
      {account === '' ? (
        <button className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={connectWallet}>Connect Wallet</button>
      ) : (
        <div className="flex items-center space-x-4">
          <div className="text-gray-700">{truncateAddress(account)}</div>
          <select
            className="bg-white border border-gray-300 rounded px-2 py-1 hover:border-gray-500"
            value={currentNetwork}
            onChange={(e) => handleNetworkChange(e.target.value)}
          >
            {networkKeys.map((key) => (
              <option key={key} value={key}>
                {networks[key].chainName}
              </option>
            ))}
          </select>
          <button className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700" onClick={disconnectWallet}>Disconnect</button>
        </div>
      )}
      {connectionError && <p className="text-red-500">{connectionError}</p>}
    </div>
  );
};

export default ConnectButton;
